import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DepositTarget } from '@pages/Traders-Room/Deposit/hooks/types'

import { Truncate } from '@ui/Truncate/Truncate'
import { Body2 } from '@ui/Typography'
import { WavyBillIcon } from '@ui/icons/WavyBill.icon'

import { useWindowResize } from '@utils/domUtils'

import { PaymentProvider } from '@model/PaymentProviderDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import styles from './FundingSummary.module.scss'

interface FundingSummaryEntity {
  currency?: { id: string }
  name?: string
}

interface Props {
  paymentProvider?: PaymentProvider
  wallet?: FundingSummaryEntity
  tradingAccount?: FundingSummaryEntity
  amount?: number | string
  walletPaymentProviderLogo?: string
  showAmount?: boolean
}

const VALUE_GAP = 8
const ICON_SIZE = 24

export const FundingSummary: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { showAmount, paymentProvider, wallet, tradingAccount, walletPaymentProviderLogo } = props
  const { formatMoney } = useFormatNumber()
  const isMobile = useWindowResize()

  const amountValue = Number(props.amount || 0)
  const providerCurrencyId = paymentProvider?.currency?.id || ''
  const paymentMethodLabel = paymentProvider?.description || paymentProvider?.name || ''
  const derivedTarget: DepositTarget = wallet ? 'wallet' : 'trading-account'
  const summaryCurrency =
    providerCurrencyId ||
    (derivedTarget === 'wallet' ? wallet?.currency?.id : tradingAccount?.currency?.id) ||
    'USD'

  const paymentMethodLogo = useMemo(
    () => walletPaymentProviderLogo ?? paymentProvider?.logo ?? <WavyBillIcon size={ICON_SIZE} />,
    [paymentProvider?.logo, walletPaymentProviderLogo]
  )

  const destinationLabel = useMemo(() => {
    if (derivedTarget === 'wallet') {
      if (!wallet?.currency?.id) return ''
      return t('Wallet.Wallet Currency Name', {
        walletCurrencyId: wallet.currency.id,
        walletName: wallet.name,
      })
    }

    if (!tradingAccount?.currency?.id) return ''
    return `${tradingAccount.name ?? ''}`
  }, [derivedTarget, t, tradingAccount, wallet])

  const rows = useMemo(() => {
    const rows = [
      {
        label: t('Wallet.Payment Method'),
        value: paymentMethodLabel,
        addon: paymentMethodLogo,
      },
      {
        label: derivedTarget === 'wallet' ? t('Wallet.Wallet') : t('Trading Account.Account'),
        value: destinationLabel,
      },
    ]

    if (showAmount) {
      rows.push({
        label: t('Wallet.Amount'),
        value: formatMoney(amountValue, summaryCurrency),
      })
    }

    return rows
  }, [
    amountValue,
    derivedTarget,
    destinationLabel,
    formatMoney,
    paymentMethodLabel,
    paymentMethodLogo,
    showAmount,
    summaryCurrency,
    t,
  ])

  const renderAddon = (addon: React.ReactNode) => {
    return typeof addon === 'string' ? (
      <img src={addon} alt='' width={ICON_SIZE} height={ICON_SIZE} />
    ) : (
      addon
    )
  }

  const getValueWidth = (hasAddon: boolean) => {
    if (hasAddon) {
      if (isMobile) {
        return `calc(100% - ${ICON_SIZE}px - ${VALUE_GAP}px)`
      }

      return 240
    }
    return 240 + ICON_SIZE + VALUE_GAP
  }

  return (
    <div className={styles.fundingSummary}>
      {rows.map((r) => (
        <div className={styles.row}>
          <Body2 color='text-low-em'>{r.label}</Body2>
          <div className={styles.value} style={{ gap: VALUE_GAP }}>
            {r.addon && <div>{renderAddon(r.addon)}</div>}

            <Box maxWidth={getValueWidth(!!r.addon)} fullWidth={!isMobile}>
              <Truncate>
                <Body2>{r.value}</Body2>
              </Truncate>
            </Box>
          </div>
        </div>
      ))}
    </div>
  )
}
